/**
* Per-locale copy for /{locale}/marriage pillar mirrors.
* Locales: en · zh · ja · ko · vi · id.
*/
export type MarriageLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface MarriageLocaleCopy {
  hreflang: string;
  title: string;
  description: string;
  h1: string;
  lead: string;
  benefits: Array<[string, string]>;
  sections: Array<{ heading: string; paragraphs: string[] }>;
  faqs: Array<{ q: string; a: string }>;
  ctaTitle: string;
  ctaBody: string;
  breadcrumbHome: string;
  breadcrumbPillar: string;
  seeServices: string;
  seeCountries: string;
}

export const MARRIAGE_LOCALE_COPY: Record<MarriageLocale, MarriageLocaleCopy> = {
  en: {
    hreflang: "en",
    title: "Thai-Foreigner Marriage & Family Lawyers | Thai Notary Law",
    description:
      "Licensed Thai family lawyers: foreign-Thai marriage registration, Non-O visa, prenups, divorce, custody, adoption, dependent visas & naturalisation.",
    h1: "Thai-Foreigner Marriage & Family Law — Bangkok Nationwide",
    lead:
      "Thai Notary Law is a family & immigration boutique that walks foreign nationals and their Thai partners through every phase of a cross-border marriage: single-status affirmation at the embassy, MoJ legalisation, registration at the amphur, prenuptial agreement drafting, Non-O marriage visa, TM.7 extensions, uncontested and contested divorce, child custody, adoption of Thai children, dependent visas for expat families and long-term naturalisation planning.",
    benefits: [
      ["Bar-registered family lawyers", "Real Thai lawyers registered with the Lawyers Council of Thailand — never agents or notary brokers."],
      ["Consular liaison", "Direct working relationships with US, UK, AU, DE, FR, JP, KR, NL, SE, CH embassies for affirmation and passport-marriage lodgment."],
      ["MoJ + amphur end-to-end", "Translation, MoJ certification and amphur registration handled the same week for Bangkok filings."],
      ["Bilingual prenup drafting", "Prenuptial agreements drafted in English + Thai, registered on the marriage certificate, enforceable in Thai and foreign courts."],
      ["Divorce & custody options", "Uncontested amphur divorce, contested court divorce and Hague Convention child abduction responses under one firm."],
      ["Post-marriage compliance", "Non-O marriage visa, TM.7 annual extension, TM.30, dependent visas, tax residency and long-term naturalisation roadmap."],
    ],
    sections: [
      {
        heading: "How Thai-foreigner marriage actually works",
        paragraphs: [
          "Step 1 — Affirmation of Freedom to Marry: the foreign partner appears at their embassy in Bangkok and swears they are single. Fee ranges from THB 2,000 (Singapore, Malaysia) to THB 15,000 (US, UK, AU). We book the slot and prepare the sworn statement.",
          "Step 2 — Ministry of Foreign Affairs (MFA) legalisation and Thai translation: the affirmation must be translated into Thai and legalised by MoJ / MFA. Our in-house translators + MFA runner turn this around in 2-3 working days (or 24 hours rush).",
          "Step 3 — Amphur registration: both parties (and two witnesses) present at any district office (amphur) in Thailand. We arrange the appointment, prepare Kor.Ror.2 / Kor.Ror.3 marriage certificates and file for embassy report-of-marriage where required (US CRBA, UK, AU, DE, etc.).",
          "Step 4 — Register the prenuptial agreement (optional but strongly recommended for cross-border couples). Once registered on Kor.Ror.2 the prenup is enforceable in Thai courts and recognised in most foreign jurisdictions.",
        ],
      },
      {
        heading: "Non-O marriage visa and annual extension",
        paragraphs: [
          "Foreign spouses of Thai citizens can apply for a 90-day Non-O and convert it to a 1-year extension (TM.7) if they show either 400,000 THB in a Thai bank for 2 months, or a combined monthly income of 40,000 THB. Extension is at the local immigration office corresponding to the applicant's registered address.",
          "We handle document collation, sponsor documents from the Thai spouse (national ID, house registration, marriage certificate), Immigration Bureau home visit and residence map. We pre-screen income proof before submission so the file meets the published criteria; the decision itself rests with the Immigration Bureau.",
        ],
      },
      {
        heading: "Divorce, custody and long-term planning",
        paragraphs: [
          "Uncontested divorce at the amphur takes one afternoon if both parties agree on assets and custody. Contested divorce at the Juvenile & Family Court runs 6-12 months with mandatory mediation before trial.",
          "Custody follows the 'best interest of the child' test. We draft parenting plans, negotiate cross-border access schedules and file Hague Convention responses when needed.",
          "Naturalisation through marriage becomes available after 3 continuous years of Non-O marriage extension + Thai language interview + tax records. We plan this from day one so nothing is left to chance.",
        ],
      },
    ],
    faqs: [
      { q: "Do we need a lawyer to marry in Thailand?", a: "Legally, no — but a lawyer streamlines affirmation, MFA legalisation, translation and amphur scheduling, and ensures your marriage is recognised back home (embassy report, apostille of the Thai marriage certificate)." },
      { q: "Is a Thai prenup enforceable abroad?", a: "A prenup registered on Kor.Ror.2 is enforceable in Thailand. For enforceability abroad we structure it with mirror clauses and dual signing so it survives US, UK, EU, AU jurisdiction tests." },
      { q: "How long does the 90-day Non-O to 1-year extension take?", a: "The initial 90-day Non-O is issued in 2-4 weeks at the Thai embassy abroad. Conversion + extension at Bangkok Immigration takes 1-2 weeks, including the mandatory home visit." },
      { q: "Can I stay in Thailand during the divorce?", a: "Yes. Your Non-O extension remains valid until expiry. We can arrange a transition to Non-O dependent, LTR, Elite or tourist visa depending on the court timeline." },
      { q: "What are the government fees?", a: "Amphur marriage registration is free for Thai nationals, THB 200 stamp duty for foreigners. Non-O visa fee is THB 2,000 (single-entry) / 5,000 (multi-entry). Extension is THB 1,900. Divorce filing is THB 2,500-4,000." },
    ],
    ctaTitle: "Book your 30-minute marriage & family consultation — free",
    ctaBody: "Phone · LINE · Email · Zoom. We map out marriage registration, prenup, Non-O visa and long-term residency in one written brief before you engage.",
    breadcrumbHome: "Home",
    breadcrumbPillar: "Marriage & Family",
    seeServices: "All marriage & family services",
    seeCountries: "By foreign spouse nationality",
  },
  zh: {
    hreflang: "zh",
    title: "泰-外跨国婚姻与家事律师 | Thai Notary Law",
    description:
      "泰国持牌家事律师团队 — 泰外结婚登记、Non-O 婚姻签证、婚前协议、离婚、子女监护、收养、家属签证、通过婚姻归化。",
    h1: "泰-外跨国婚姻与家事 — 全泰服务",
    lead:
      "Thai Notary Law 是一家家事与移民精品所,为外国人及其泰国配偶提供跨国婚姻全流程服务:大使馆单身证明、司法部/外交部认证、区公所登记、婚前协议起草、Non-O 婚姻签证、TM.7 年度延签、协议或诉讼离婚、子女监护、泰籍儿童收养、外派家庭家属签证以及通过婚姻的长期归化规划。",
    benefits: [
      ["泰国律师公会持牌家事律师", "真律师,非中介或代办。"],
      ["领事联络", "与美 · 英 · 澳 · 德 · 法 · 日 · 韩 · 荷 · 瑞 · 瑞士各大使馆直接协调。"],
      ["司法部 + 区公所一站办", "曼谷区公所翻译、司法部认证与登记同一周完成。"],
      ["双语婚前协议", "英泰双语婚前协议,登记在结婚证 Kor.Ror.2 上,在泰及境外法院均可执行。"],
      ["离婚与监护多选项", "协议离婚(区公所)、诉讼离婚(少年家事法院)、海牙公约儿童诱拐应对,同一律所处理。"],
      ["婚后合规", "Non-O 婚姻签证、TM.7 续签、TM.30、家属签证、税务居民及长期归化路线图。"],
    ],
    sections: [
      {
        heading: "泰-外结婚实际流程",
        paragraphs: [
          "第 1 步 — 单身证明:外国配偶到曼谷本国使馆宣誓单身。费用从新加坡/马来西亚约 2,000 泰铢到美/英/澳约 15,000 泰铢。我们代订时段并准备宣誓文本。",
          "第 2 步 — 外交部认证与泰文翻译:单身证明须翻译成泰文并由司法部/外交部认证。内部翻译加外交部代跑,2-3 个工作日完成(加急 24 小时)。",
          "第 3 步 — 区公所登记:双方(加两名证人)于泰国任一区公所办理。我们安排预约、准备 Kor.Ror.2 / Kor.Ror.3 证书,并在必要时向大使馆报备结婚(美 CRBA、英、澳、德等)。",
          "第 4 步 — 登记婚前协议(可选,跨国夫妻强烈建议)。一经登记在 Kor.Ror.2 上,泰国法院即可执行,并在多数境外司法域获得承认。",
        ],
      },
      {
        heading: "Non-O 婚姻签证与年度续签",
        paragraphs: [
          "泰籍配偶的外国配偶可申请 90 天 Non-O 并转 1 年延签(TM.7),需在泰国银行存 40 万泰铢 2 个月,或月综合收入 4 万泰铢。续签在申请人登记住址所属地移民办公室办理。",
          "我们处理泰配偶身份证、户籍、结婚证等文件、担保文件、移民局家访和住址地图。我们在递交前预审收入证明，确保材料符合公布的条件；核准与否由移民局决定。",
        ],
      },
      {
        heading: "离婚、监护与长期规划",
        paragraphs: [
          "协议离婚在区公所一个下午完成(前提是双方就财产与监护达成一致)。诉讼离婚在少年家事法院需 6-12 个月,并须在庭审前调解。",
          "监护适用『子女最佳利益』标准。我们起草育儿计划、协商跨境探视时间表,必要时应对海牙公约申请。",
          "通过婚姻归化需连续 3 年持 Non-O 婚姻延签 + 泰语面试 + 纳税记录。我们从第一天起就规划,不留意外。",
        ],
      },
    ],
    faqs: [
      { q: "在泰结婚需要律师吗?", a: "法律上不需要,但律师可加速单身证明、外交部认证、翻译与区公所预约,并确保您的婚姻在母国获得承认(领事报备、结婚证加签)。" },
      { q: "泰国婚前协议在境外有效吗?", a: "登记在 Kor.Ror.2 上的婚前协议在泰国可执行。境外方面我们采用镜像条款和双签设计,以通过美 · 英 · 欧盟 · 澳的司法审查。" },
      { q: "90 天 Non-O 转 1 年续签需多久?", a: "首次 90 天 Non-O 在境外泰使馆 2-4 周签发。曼谷移民局转签+续签 1-2 周,含强制家访。" },
      { q: "离婚期间可以留在泰国吗?", a: "可以。Non-O 延签在有效期内继续有效。可根据法院进度转 Non-O 家属、LTR、精英卡或旅游签。" },
      { q: "政府费用有哪些?", a: "区公所结婚登记泰国人免费,外国人 200 泰铢印花税。Non-O 签证费单次 2,000 / 多次 5,000 泰铢。续签 1,900 泰铢。离婚受理费 2,500-4,000 泰铢。" },
    ],
    ctaTitle: "预约 30 分钟免费跨国婚姻与家事咨询",
    ctaBody: "电话 · LINE · 邮件 · Zoom。委托前我们提供书面结婚登记、婚前协议、Non-O 签证与长期居留一体化方案。",
    breadcrumbHome: "首页",
    breadcrumbPillar: "婚姻与家事",
    seeServices: "所有婚姻与家事服务",
    seeCountries: "按外籍配偶国籍",
  },
  ja: {
    hreflang: "ja",
    title: "タイ人-外国人 国際結婚・家事弁護士 | Thai Notary Law",
    description:
      "タイ登録家事弁護士による外国人-タイ人結婚登録、Non-O 結婚ビザ、婚前契約、離婚、親権、養子縁組、扶養ビザ、婚姻による帰化まで一括対応。",
    h1: "タイ人-外国人 国際結婚と家事法 — 全国対応",
    lead:
      "Thai Notary Law は、外国人とタイ人パートナーによる国際結婚のあらゆる段階を伴走する家事・移民ブティック法律事務所です:大使館での独身証明、司法省/外務省認証、区役所での結婚登録、英泰バイリンガル婚前契約、Non-O 結婚ビザ、TM.7 年次延長、協議・訴訟離婚、親権、タイ人子の養子縁組、駐在家族の扶養ビザ、そして長期の帰化計画までカバーします。",
    benefits: [
      ["タイ弁護士会登録の家事弁護士", "エージェントや公証代行ではなく、本物のタイ弁護士。"],
      ["領事連携", "米・英・豪・独・仏・日・韓・蘭・瑞・スイス各大使館と直接連携。"],
      ["司法省+区役所ワンストップ", "翻訳、司法省認証、区役所登録を同週で処理(バンコク圏)。"],
      ["英泰婚前契約書作成", "Kor.Ror.2 婚姻証書上に登録し、タイ・国外法廷双方で執行可能。"],
      ["離婚と親権の複数オプション", "区役所協議離婚、家庭裁判所訴訟離婚、ハーグ条約対応まで一つの事務所で。"],
      ["結婚後コンプライアンス", "Non-O 結婚ビザ、TM.7 年次延長、TM.30、扶養ビザ、税務居住、長期帰化ロードマップ。"],
    ],
    sections: [
      {
        heading: "タイ人-外国人結婚の実際の流れ",
        paragraphs: [
          "ステップ1 — 独身証明:外国人配偶者がバンコクの自国大使館に出向き独身を宣誓。費用は約 2,000 バーツ(星・馬)から約 15,000 バーツ(米・英・豪)。予約と宣誓書作成を代行。",
          "ステップ2 — 外務省認証+タイ語翻訳:独身証明はタイ語に翻訳のうえ司法省/外務省で認証。内部翻訳+外務省ランナー体制で 2-3 営業日(特急 24 時間)。",
          "ステップ3 — 区役所登記:両当事者と証人 2 名がタイ国内の任意の区役所へ。予約手配、Kor.Ror.2/Kor.Ror.3 の作成、必要に応じて大使館への結婚報告(米 CRBA、英、豪、独等)まで対応。",
          "ステップ4 — 婚前契約書の登録(任意、国際結婚では強く推奨)。Kor.Ror.2 に登録された婚前契約はタイの裁判所で執行可能、多くの外国司法域でも承認されます。",
        ],
      },
      {
        heading: "Non-O 結婚ビザと年次延長",
        paragraphs: [
          "タイ人配偶者の外国人配偶者は 90 日 Non-O を取得後、タイ銀行に 40 万バーツを 2 か月保持、または合算月収 4 万バーツで 1 年延長(TM.7)へ切替可。延長は申請者住所地の入管で処理。",
          "タイ人配偶者側書類(身分証・戸籍・結婚証)、収入証明、入管家庭訪問、住所地図まで代行。事前スクリーニングにより却下率 3% 未満。",
        ],
      },
      {
        heading: "離婚、親権、長期計画",
        paragraphs: [
          "区役所協議離婚は資産と親権について合意があれば午後半日で完了。家庭裁判所訴訟離婚は 6-12 か月、必ず調停前置。",
          "親権は『子の最善の利益』基準で判断。育児計画書の作成、越境面会スケジュールの交渉、必要に応じたハーグ条約対応を実施。",
          "婚姻による帰化は Non-O 結婚延長 3 年連続 + タイ語面接 + 納税実績で可能。初日から計画し不確定要素をなくします。",
        ],
      },
    ],
    faqs: [
      { q: "タイでの結婚に弁護士は必要?", a: "法律上は不要ですが、独身証明・外務省認証・翻訳・区役所予約が加速し、母国での承認(領事報告・結婚証アポスティーユ)も確実になります。" },
      { q: "タイの婚前契約は海外でも有効?", a: "Kor.Ror.2 に登録された婚前契約はタイで執行可能。海外向けにはミラー条項と二重署名を用い、米・英・EU・豪の司法審査に耐える構造にします。" },
      { q: "90 日 Non-O から 1 年延長までの期間は?", a: "初回 Non-O は在外タイ大使館で 2-4 週間で発給。切替+延長はバンコク入管で 1-2 週間(家庭訪問含む)。" },
      { q: "離婚中もタイに滞在可能?", a: "はい。Non-O 延長は有効期限まで継続。裁判の進行に応じて Non-O 扶養・LTR・エリート・観光ビザへ切り替え可能。" },
      { q: "政府手数料は?", a: "区役所結婚登録はタイ人無料、外国人は印紙税 200 バーツ。Non-O 費は単次 2,000/複数 5,000 バーツ。延長 1,900 バーツ。離婚提訴 2,500-4,000 バーツ。" },
    ],
    ctaTitle: "30 分間の無料 国際結婚・家事相談を予約",
    ctaBody: "電話 ・ LINE ・ メール ・ Zoom。ご依頼前に結婚登記・婚前契約・Non-O ビザ・長期居住を一つの書面ブリーフで提示。",
    breadcrumbHome: "ホーム",
    breadcrumbPillar: "結婚と家族",
    seeServices: "結婚・家事サービス一覧",
    seeCountries: "外国人配偶者国籍別",
  },
  ko: {
    hreflang: "ko",
    title: "태국-외국인 국제결혼 및 가족법 변호사 | Thai Notary Law",
    description:
      "태국 등록 가족법 변호사가 외국인-태국인 결혼 등록, Non-O 결혼 비자, 혼전 계약, 이혼, 양육권, 입양, 부양 비자, 결혼을 통한 귀화까지 원스톱 지원.",
    h1: "태국-외국인 국제결혼 및 가족법 — 전국 서비스",
    lead:
      "Thai Notary Law는 외국인과 태국인 파트너의 국제결혼 전 과정을 동행하는 가족·이민 부티크 로펌입니다. 대사관 독신 증명, 법무부/외교부 인증, 구청 등록, 영-태 이중 언어 혼전 계약, Non-O 결혼 비자, TM.7 연간 연장, 협의·소송 이혼, 양육권, 태국 아동 입양, 주재원 가족 부양 비자, 결혼을 통한 장기 귀화 계획까지 커버합니다.",
    benefits: [
      ["태국 변호사협회 등록 가족법 변호사", "에이전트나 공증 브로커가 아닌 진짜 태국 변호사."],
      ["영사 협력", "미·영·호·독·프·일·한·네·스웨·스위스 대사관과 직접 업무 관계."],
      ["법무부+구청 일괄", "번역, 법무부 인증, 구청 등록을 방콕 기준 같은 주에 처리."],
      ["이중 언어 혼전 계약 작성", "Kor.Ror.2 결혼증서에 등록, 태국 및 외국 법원에서 집행 가능."],
      ["이혼·양육권 다양한 옵션", "구청 협의 이혼, 가정법원 소송 이혼, 헤이그 아동 유괴 대응까지 한 로펌에서."],
      ["결혼 후 컴플라이언스", "Non-O 결혼 비자, TM.7 연간 연장, TM.30, 부양 비자, 세무 거주, 장기 귀화 로드맵."],
    ],
    sections: [
      {
        heading: "태국-외국인 결혼의 실제 절차",
        paragraphs: [
          "1단계 — 독신 증명: 외국인 배우자가 방콕 소재 자국 대사관에 출석해 독신 선서. 비용은 싱가포르/말레이시아 약 2,000 바트에서 미·영·호 약 15,000 바트. 예약과 선서서 준비를 대행합니다.",
          "2단계 — 외교부 인증 및 태국어 번역: 독신 증명은 태국어로 번역 후 법무부/외교부 인증 필요. 사내 번역+외교부 러너로 2-3 영업일 (긴급 24시간).",
          "3단계 — 구청 등록: 양 당사자와 증인 2명이 태국 내 임의의 구청 방문. 예약, Kor.Ror.2/Kor.Ror.3 작성, 필요 시 대사관 결혼 보고(미 CRBA, 영, 호, 독 등)까지 대행.",
          "4단계 — 혼전 계약 등록(선택이지만 국제 부부는 강력 권장). Kor.Ror.2에 등록되면 태국 법원에서 집행 가능, 대부분 외국 관할에서도 인정.",
        ],
      },
      {
        heading: "Non-O 결혼 비자와 연간 연장",
        paragraphs: [
          "태국인 배우자의 외국인 배우자는 90일 Non-O 취득 후, 태국 은행에 40만 바트 2개월 예치 또는 합산 월 소득 4만 바트로 1년 연장(TM.7) 전환 가능. 연장은 신청자 주소지 관할 이민 사무소에서 처리.",
          "태국 배우자 신분증·주민등록·결혼증 등 서류, 소득 증빙, 이민국 가정 방문, 주소 지도까지 대행. 사전 심사로 거절율 3% 미만.",
        ],
      },
      {
        heading: "이혼, 양육권, 장기 계획",
        paragraphs: [
          "구청 협의 이혼은 재산과 양육권 합의 시 오후 반나절에 완료. 가정법원 소송 이혼은 6-12개월, 재판 전 조정 필수.",
          "양육권은 '자녀의 최선의 이익' 기준. 육아 계획서 작성, 국경 간 방문 일정 협상, 필요 시 헤이그 협약 대응.",
          "결혼을 통한 귀화는 Non-O 결혼 연장 3년 연속 + 태국어 면접 + 납세 기록으로 가능. 첫날부터 계획해 불확실성 제거.",
        ],
      },
    ],
    faqs: [
      { q: "태국에서 결혼하려면 변호사가 필요한가요?", a: "법적으로는 불필요하나 독신 증명·외교부 인증·번역·구청 예약을 가속화하고, 본국 인정(영사 보고, 결혼증 아포스티유)도 확보합니다." },
      { q: "태국 혼전 계약이 해외에서도 유효한가요?", a: "Kor.Ror.2에 등록된 혼전 계약은 태국에서 집행 가능. 해외 대응을 위해 미러 조항과 이중 서명을 사용해 미·영·EU·호주 사법 심사를 통과하도록 설계." },
      { q: "90일 Non-O에서 1년 연장까지 얼마나 걸리나요?", a: "최초 Non-O는 해외 태국 대사관에서 2-4주. 방콕 이민국 전환+연장은 1-2주, 가정 방문 포함." },
      { q: "이혼 중에도 태국에 머무를 수 있나요?", a: "네. Non-O 연장은 만료까지 유효. 재판 진행에 따라 Non-O 부양, LTR, 엘리트, 관광 비자로 전환 가능." },
      { q: "정부 수수료는?", a: "구청 결혼 등록은 태국인 무료, 외국인 인지세 200 바트. Non-O 비자는 단수 2,000/복수 5,000 바트. 연장 1,900 바트. 이혼 제소 2,500-4,000 바트." },
    ],
    ctaTitle: "30분 무료 국제결혼·가족법 상담 예약",
    ctaBody: "전화 · LINE · 이메일 · Zoom. 위임 전 결혼 등록·혼전 계약·Non-O 비자·장기 거주를 하나의 서면 브리프로 제공.",
    breadcrumbHome: "홈",
    breadcrumbPillar: "결혼 및 가족",
    seeServices: "결혼 및 가족법 서비스 전체",
    seeCountries: "외국인 배우자 국적별",
  },
  vi: {
    hreflang: "vi",
    title: "Luật sư Hôn nhân & Gia đình Thái-Nước ngoài",
    description:
      "Luật sư gia đình cấp phép: đăng ký kết hôn Thái-Nước ngoài, visa Non-O, tiền hôn nhân, ly hôn, quyền nuôi con, nhận con nuôi, visa phụ thuộc.",
    h1: "Hôn nhân & Gia đình Thái-Nước ngoài — Toàn quốc",
    lead:
      "Thai Notary Law là hãng luật gia đình & di trú đồng hành cùng người nước ngoài và bạn đời Thái qua mọi giai đoạn hôn nhân xuyên biên giới: xác nhận độc thân tại đại sứ quán, hợp pháp hóa Bộ Tư pháp, đăng ký tại quận (amphur), soạn hợp đồng tiền hôn nhân song ngữ, visa hôn nhân Non-O, gia hạn TM.7, ly hôn thuận tình hoặc tranh chấp, quyền nuôi con, nhận con nuôi Thái, visa phụ thuộc cho gia đình xa xứ và lộ trình nhập tịch dài hạn.",
    benefits: [
      ["Luật sư gia đình đăng ký Hiệp hội Luật sư Thái", "Luật sư thực sự, không phải môi giới hay đại lý công chứng."],
      ["Liên lạc lãnh sự", "Quan hệ trực tiếp với đại sứ quán Mỹ, Anh, Úc, Đức, Pháp, Nhật, Hàn, Hà Lan, Thụy Điển, Thụy Sĩ."],
      ["Bộ Tư pháp + quận trọn gói", "Dịch, hợp pháp hóa MoJ và đăng ký tại quận (khu vực Bangkok) trong cùng tuần."],
      ["Soạn hợp đồng tiền hôn nhân song ngữ", "Đăng ký trên giấy đăng ký kết hôn Kor.Ror.2, có hiệu lực tại tòa Thái và tòa nước ngoài."],
      ["Ly hôn & quyền nuôi con đa lựa chọn", "Ly hôn thuận tình tại quận, ly hôn tranh chấp tại Tòa Vị Thành Niên & Gia Đình, đáp ứng Công ước Hague — cùng một hãng luật."],
      ["Tuân thủ sau hôn nhân", "Visa hôn nhân Non-O, gia hạn TM.7 hàng năm, TM.30, visa phụ thuộc, cư trú thuế, lộ trình nhập tịch dài hạn."],
    ],
    sections: [
      {
        heading: "Quy trình kết hôn Thái-Nước ngoài",
        paragraphs: [
          "Bước 1 — Xác nhận độc thân: bạn đời nước ngoài đến đại sứ quán nước mình tại Bangkok để tuyên thệ độc thân. Phí từ ~2.000 THB (Singapore, Malaysia) đến ~15.000 THB (Mỹ, Anh, Úc). Chúng tôi đặt lịch và chuẩn bị tuyên thệ.",
          "Bước 2 — Hợp pháp hóa Bộ Ngoại giao (MFA) và dịch tiếng Thái: xác nhận độc thân được dịch sang tiếng Thái và hợp pháp hóa qua MoJ/MFA. Đội dịch nội bộ + runner MFA hoàn thành trong 2-3 ngày làm việc (khẩn 24 giờ).",
          "Bước 3 — Đăng ký tại quận (amphur): cả hai bên và 2 người làm chứng có mặt tại bất kỳ quận nào ở Thái Lan. Chúng tôi đặt lịch, chuẩn bị Kor.Ror.2/Kor.Ror.3 và báo cáo hôn nhân về đại sứ quán (Mỹ CRBA, Anh, Úc, Đức, v.v.).",
          "Bước 4 — Đăng ký hợp đồng tiền hôn nhân (tùy chọn, khuyến nghị mạnh cho cặp đôi xuyên biên giới). Khi đã đăng ký trên Kor.Ror.2, hợp đồng có hiệu lực tại tòa Thái và được công nhận ở hầu hết các khu vực pháp lý nước ngoài.",
        ],
      },
      {
        heading: "Visa hôn nhân Non-O và gia hạn hàng năm",
        paragraphs: [
          "Bạn đời nước ngoài của công dân Thái xin Non-O 90 ngày rồi chuyển sang gia hạn 1 năm (TM.7) khi có 400.000 THB trong ngân hàng Thái 2 tháng, hoặc thu nhập kết hợp 40.000 THB/tháng. Gia hạn tại văn phòng di trú tương ứng với địa chỉ đăng ký.",
          "Chúng tôi xử lý hồ sơ, giấy tờ bảo lãnh từ vợ/chồng Thái (CCCD, sổ hộ khẩu, giấy đăng ký kết hôn), thăm nhà của Cục Di trú và bản đồ nơi cư trú. Chúng tôi sàng lọc chứng minh thu nhập trước khi nộp để hồ sơ đáp ứng tiêu chí công bố; quyết định thuộc về Cục Di trú.",
        ],
      },
      {
        heading: "Ly hôn, quyền nuôi con và kế hoạch dài hạn",
        paragraphs: [
          "Ly hôn thuận tình tại quận diễn ra trong một buổi chiều nếu hai bên đồng ý về tài sản và quyền nuôi con. Ly hôn tranh chấp tại Tòa Vị Thành Niên & Gia Đình kéo dài 6-12 tháng với hòa giải bắt buộc trước xét xử.",
          "Quyền nuôi con theo tiêu chuẩn 'lợi ích tốt nhất của trẻ'. Chúng tôi soạn kế hoạch nuôi dạy, đàm phán lịch thăm nom xuyên biên giới và phản hồi Công ước Hague khi cần.",
          "Nhập tịch qua hôn nhân khả dụng sau 3 năm gia hạn Non-O hôn nhân liên tục + phỏng vấn tiếng Thái + hồ sơ thuế. Chúng tôi lập kế hoạch từ ngày đầu để không có điều gì bỏ ngỏ.",
        ],
      },
    ],
    faqs: [
      { q: "Có cần luật sư để kết hôn tại Thái Lan?", a: "Luật không bắt buộc, nhưng luật sư sẽ đẩy nhanh xác nhận độc thân, MFA, dịch, lịch hẹn quận, và đảm bảo hôn nhân của bạn được công nhận tại quê nhà (báo cáo lãnh sự, apostille giấy đăng ký kết hôn Thái)." },
      { q: "Hợp đồng tiền hôn nhân Thái có hiệu lực ở nước ngoài?", a: "Hợp đồng đăng ký trên Kor.Ror.2 có hiệu lực tại Thái. Đối với nước ngoài, chúng tôi cấu trúc bằng điều khoản gương và ký kép để vượt kiểm tra Mỹ, Anh, EU, Úc." },
      { q: "Bao lâu từ Non-O 90 ngày lên gia hạn 1 năm?", a: "Non-O 90 ngày ban đầu cấp tại đại sứ quán Thái ở nước ngoài trong 2-4 tuần. Chuyển đổi + gia hạn tại Di trú Bangkok mất 1-2 tuần, bao gồm thăm nhà bắt buộc." },
      { q: "Có được ở lại Thái Lan trong thời gian ly hôn?", a: "Có. Gia hạn Non-O vẫn có hiệu lực đến hết hạn. Có thể chuyển sang Non-O phụ thuộc, LTR, Elite hoặc visa du lịch tùy tiến độ tòa án." },
      { q: "Phí nhà nước ra sao?", a: "Đăng ký kết hôn tại quận miễn phí cho công dân Thái, tem thuế 200 THB cho người nước ngoài. Phí visa Non-O 2.000 (đơn) / 5.000 THB (nhiều lần). Gia hạn 1.900 THB. Đơn ly hôn 2.500-4.000 THB." },
    ],
    ctaTitle: "Đặt lịch tư vấn hôn nhân & gia đình 30 phút miễn phí",
    ctaBody: "Điện thoại · LINE · Email · Zoom. Chúng tôi cung cấp bản tóm tắt bằng văn bản về đăng ký kết hôn, hợp đồng tiền hôn nhân, visa Non-O và cư trú dài hạn trước khi bạn ký hợp đồng.",
    breadcrumbHome: "Trang chủ",
    breadcrumbPillar: "Hôn nhân & Gia đình",
    seeServices: "Tất cả dịch vụ hôn nhân & gia đình",
    seeCountries: "Theo quốc tịch vợ/chồng nước ngoài",
  },
  id: {
    hreflang: "id",
    title: "Pengacara Pernikahan & Keluarga Thai-Asing | Thai Notary Law",
    description:
      "Pengacara keluarga berlisensi: registrasi nikah Thai-Asing, visa Non-O, perjanjian pra-nikah, cerai, hak asuh, adopsi, visa tanggungan, naturalisasi.",
    h1: "Pernikahan Thai-Asing & Hukum Keluarga — Nasional",
    lead:
      "Thai Notary Law adalah firma butik keluarga & imigrasi yang mendampingi warga asing dan pasangan Thai mereka melalui setiap fase pernikahan lintas batas: pernyataan status lajang di kedutaan, legalisasi Kementerian Kehakiman, registrasi di kantor distrik (amphur), penyusunan perjanjian pra-nikah dwibahasa, visa pernikahan Non-O, perpanjangan TM.7 tahunan, perceraian tanpa dan dengan sengketa, hak asuh anak, adopsi anak Thai, visa tanggungan untuk keluarga ekspatriat, dan rencana naturalisasi jangka panjang.",
    benefits: [
      ["Pengacara keluarga terdaftar Lawyers Council of Thailand", "Pengacara sungguhan, bukan agen atau broker notaris."],
      ["Kerja sama konsuler", "Hubungan langsung dengan kedutaan AS, Inggris, Australia, Jerman, Prancis, Jepang, Korea, Belanda, Swedia, Swiss."],
      ["MoJ + amphur satu paket", "Terjemahan, sertifikasi MoJ, dan registrasi amphur ditangani dalam minggu yang sama untuk Bangkok."],
      ["Perjanjian pra-nikah dwibahasa", "Perjanjian pra-nikah dalam bahasa Inggris + Thai, terdaftar pada sertifikat pernikahan, dapat ditegakkan di pengadilan Thai dan asing."],
      ["Opsi perceraian & hak asuh", "Perceraian tanpa sengketa di amphur, perceraian dengan sengketa di Pengadilan Keluarga & Anak, respons Konvensi Hague — dalam satu firma."],
      ["Kepatuhan pasca-pernikahan", "Visa pernikahan Non-O, perpanjangan TM.7, TM.30, visa tanggungan, residensi pajak, dan peta jalan naturalisasi jangka panjang."],
    ],
    sections: [
      {
        heading: "Bagaimana pernikahan Thai-Asing bekerja",
        paragraphs: [
          "Langkah 1 — Pernyataan Status Lajang: pasangan asing hadir di kedutaan mereka di Bangkok dan bersumpah lajang. Biaya berkisar THB 2.000 (Singapura, Malaysia) hingga THB 15.000 (AS, Inggris, Australia). Kami memesan slot dan menyiapkan pernyataan sumpah.",
          "Langkah 2 — Legalisasi Kementerian Luar Negeri (MFA) dan terjemahan Thai: pernyataan harus diterjemahkan ke Thai dan disahkan oleh MoJ/MFA. Penerjemah in-house + runner MFA menyelesaikannya dalam 2-3 hari kerja (kilat 24 jam).",
          "Langkah 3 — Registrasi amphur: kedua pihak (dan dua saksi) hadir di kantor distrik mana pun di Thailand. Kami mengatur janji, menyiapkan sertifikat Kor.Ror.2 / Kor.Ror.3 dan mengajukan laporan pernikahan ke kedutaan jika diperlukan (AS CRBA, Inggris, Australia, Jerman, dll.).",
          "Langkah 4 — Registrasi perjanjian pra-nikah (opsional tetapi sangat direkomendasikan untuk pasangan lintas batas). Setelah terdaftar pada Kor.Ror.2, perjanjian ini dapat ditegakkan di pengadilan Thai dan diakui di sebagian besar yurisdiksi asing.",
        ],
      },
      {
        heading: "Visa pernikahan Non-O dan perpanjangan tahunan",
        paragraphs: [
          "Pasangan asing dari warga negara Thai dapat mengajukan Non-O 90 hari dan mengonversinya menjadi perpanjangan 1 tahun (TM.7) jika menunjukkan salah satu dari 400.000 THB di bank Thai selama 2 bulan, atau pendapatan gabungan bulanan 40.000 THB. Perpanjangan dilakukan di kantor imigrasi setempat sesuai alamat terdaftar pemohon.",
          "Kami menangani penataan dokumen, dokumen sponsor dari pasangan Thai (KTP, buku keluarga, akta nikah), kunjungan rumah Biro Imigrasi, dan peta domisili. Tingkat penolakan di bawah 3% karena kami menyaring bukti pendapatan sebelum pengajuan.",
        ],
      },
      {
        heading: "Perceraian, hak asuh, dan perencanaan jangka panjang",
        paragraphs: [
          "Perceraian tanpa sengketa di amphur memakan satu sore jika kedua pihak sepakat mengenai aset dan hak asuh. Perceraian dengan sengketa di Pengadilan Keluarga & Anak berlangsung 6-12 bulan dengan mediasi wajib sebelum persidangan.",
          "Hak asuh mengikuti standar 'kepentingan terbaik anak'. Kami menyusun rencana pengasuhan, menegosiasikan jadwal akses lintas batas, dan mengajukan respons Konvensi Hague bila perlu.",
          "Naturalisasi melalui pernikahan tersedia setelah 3 tahun perpanjangan Non-O pernikahan berturut-turut + wawancara bahasa Thai + catatan pajak. Kami merencanakannya sejak hari pertama sehingga tidak ada yang tertinggal.",
        ],
      },
    ],
    faqs: [
      { q: "Apakah perlu pengacara untuk menikah di Thailand?", a: "Secara hukum tidak — tetapi pengacara mempercepat pernyataan lajang, legalisasi MFA, terjemahan dan penjadwalan amphur, serta memastikan pernikahan Anda diakui di negara asal (laporan kedutaan, apostille akta nikah Thai)." },
      { q: "Apakah perjanjian pra-nikah Thai dapat ditegakkan di luar negeri?", a: "Perjanjian yang terdaftar pada Kor.Ror.2 dapat ditegakkan di Thailand. Untuk luar negeri, kami menyusun dengan klausul cermin dan penandatanganan ganda agar bertahan pada uji yurisdiksi AS, Inggris, UE, Australia." },
      { q: "Berapa lama Non-O 90 hari hingga perpanjangan 1 tahun?", a: "Non-O 90 hari awal diterbitkan 2-4 minggu di kedutaan Thailand di luar negeri. Konversi + perpanjangan di Imigrasi Bangkok memakan 1-2 minggu, termasuk kunjungan rumah wajib." },
      { q: "Bisakah saya tinggal di Thailand selama perceraian?", a: "Ya. Perpanjangan Non-O Anda tetap berlaku hingga kedaluwarsa. Kami dapat mengatur transisi ke Non-O tanggungan, LTR, Elite, atau visa turis tergantung jadwal pengadilan." },
      { q: "Berapa biaya pemerintah?", a: "Registrasi pernikahan amphur gratis untuk warga Thai, bea meterai THB 200 untuk warga asing. Biaya visa Non-O THB 2.000 (single-entry) / 5.000 (multi-entry). Perpanjangan THB 1.900. Pengajuan cerai THB 2.500-4.000." },
    ],
    ctaTitle: "Jadwalkan konsultasi pernikahan & keluarga 30 menit gratis",
    ctaBody: "Telepon · LINE · Email · Zoom. Kami memetakan registrasi pernikahan, perjanjian pra-nikah, visa Non-O, dan residensi jangka panjang dalam satu ringkasan tertulis sebelum Anda menandatangani.",
    breadcrumbHome: "Beranda",
    breadcrumbPillar: "Pernikahan & Keluarga",
    seeServices: "Semua layanan pernikahan & keluarga",
    seeCountries: "Berdasarkan kewarganegaraan pasangan asing",
  },
};

export const MARRIAGE_HREFLANG_LINKS = [
  { rel: "alternate" as const, hreflang: "th", href: "https://thainotarylaw.com/marriage" },
  { rel: "alternate" as const, hreflang: "en", href: "https://thainotarylaw.com/en/marriage" },
  { rel: "alternate" as const, hreflang: "zh", href: "https://thainotarylaw.com/zh/marriage" },
  { rel: "alternate" as const, hreflang: "ja", href: "https://thainotarylaw.com/ja/marriage" },
  { rel: "alternate" as const, hreflang: "ko", href: "https://thainotarylaw.com/ko/marriage" },
  { rel: "alternate" as const, hreflang: "vi", href: "https://thainotarylaw.com/vi/marriage" },
  { rel: "alternate" as const, hreflang: "id", href: "https://thainotarylaw.com/id/marriage" },
  { rel: "alternate" as const, hreflang: "x-default", href: "https://thainotarylaw.com/marriage" },
];
